/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * Theme utilities
 *
 * Goals of this rewrite:
 * - Set CSS variables to raw HSL components (e.g., "270 30% 5%"), NOT "hsl(...)"
 *   because CSS uses them as: hsl(var(--background))
 * - Single-pass application of preset variables, with optional dark overrides
 * - Robust custom theme support (hex/hsl()/components accepted)
 * - Remove duplicate/contradictory logic that caused broken themes
 */

import { themePresets } from './theme-presets';
import { lsGet, lsSet, lsRemove } from './safe-storage';

const THEME_KEY = 'theme-preset';
const CUSTOM_KEY = 'custom-theme';
const DETECTION_KEY = 'theme-detection';
const DB_PRESETS_KEY = 'db-theme-presets';

type RuntimeThemePreset = {
  name: string;
  type: string;
  variables: Record<string, string>;
  dark?: Record<string, string>;
  isDefault?: boolean;
};

let runtimeThemePresets: RuntimeThemePreset[] | null = null;

function mergeThemePresets(
  builtin: RuntimeThemePreset[],
  runtime: RuntimeThemePreset[]
): RuntimeThemePreset[] {
  const mergedByName = builtin.reduce(
    (acc, preset) => ({ ...acc, [preset.name]: preset }),
    {} as Record<string, RuntimeThemePreset>
  );

  runtime.forEach((preset) => {
    mergedByName[preset.name] = preset;
  });

  return Object.values(mergedByName);
}

function getRuntimePresetsFromStorage(): RuntimeThemePreset[] {
  const raw = lsGet(DB_PRESETS_KEY);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (item): item is RuntimeThemePreset =>
        Boolean(item) &&
        typeof item === 'object' &&
        typeof (item as RuntimeThemePreset).name === 'string' &&
        typeof (item as RuntimeThemePreset).type === 'string' &&
        typeof (item as RuntimeThemePreset).variables === 'object' &&
        (typeof (item as RuntimeThemePreset).isDefault === 'boolean' ||
          (item as RuntimeThemePreset).isDefault === undefined)
    );
  } catch {
    return [];
  }
}

function getAvailableThemePresets(): RuntimeThemePreset[] {
  const runtime = runtimeThemePresets ?? getRuntimePresetsFromStorage();
  if (runtime.length === 0) return themePresets;
  return mergeThemePresets(themePresets, runtime);
}

function getDefaultPresetName(presets: RuntimeThemePreset[]): string {
  const runtimeDefault = presets.find((preset) => preset.isDefault === true);
  if (runtimeDefault?.name) return runtimeDefault.name;

  const builtinDefault = presets.find((preset) => preset.name === 'Default');
  if (builtinDefault?.name) return builtinDefault.name;

  return presets[0]?.name ?? 'Default';
}

export function setRuntimeThemePresets(presets: RuntimeThemePreset[]): void {
  runtimeThemePresets = presets;
  lsSet(DB_PRESETS_KEY, JSON.stringify(presets));
}

// Convert a hex color string (#rgb | #rrggbb) to HSL components: "h s% l%"
function hexToHSL(hex: string): string {
  const clean = hex.replace('#', '').trim();
  let r = 0;
  let g = 0;
  let b = 0;

  if (clean.length === 3) {
    r = Number.parseInt(clean[0] + clean[0], 16);
    g = Number.parseInt(clean[1] + clean[1], 16);
    b = Number.parseInt(clean[2] + clean[2], 16);
  } else if (clean.length === 6) {
    r = Number.parseInt(clean.slice(0, 2), 16);
    g = Number.parseInt(clean.slice(2, 4), 16);
    b = Number.parseInt(clean.slice(4, 6), 16);
  } else {
    // Not a recognized hex, return a safe default
    return '0 0% 0%';
  }

  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: {
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      }
      case g: {
        h = (b - r) / d + 2;
        break;
      }
      case b: {
        h = (r - g) / d + 4;
        break;
      }
      default: {
        h = 0;
      }
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
}

/**
 * Converts RGB/RGBA to HSL components
 */
function rgbToHSL(r: number, g: number, b: number): string {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case rn: {
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      }
      case gn: {
        h = (bn - rn) / d + 2;
        break;
      }
      case bn: {
        h = (rn - gn) / d + 4;
        break;
      }
      default: {
        h = 0;
      }
    }

    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(
    l * 100
  )}%`;
}

function parseRgbToHsl(value: string): string | null {
  const match = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!match) return null;

  const r = Number.parseInt(match[1] || '0', 10);
  const g = Number.parseInt(match[2] || '0', 10);
  const b = Number.parseInt(match[3] || '0', 10);

  return rgbToHSL(r, g, b);
}

function looksLikeHslComponents(value: string): boolean {
  if (
    (value.includes('%') || value.includes(',')) &&
    !value.toLowerCase().startsWith('rgb')
  ) {
    return true;
  }

  const parts = value.split(/\s+/);
  return (
    parts.length === 3 && parts[1]?.includes('%') && parts[2]?.includes('%')
  );
}

/**
 * Normalizes any color string into HSL components:
 * - "#rrggbb" or "#rgb" -> "h s% l%"
 * - "hsl(...)" -> returns the inner content (e.g., "h s% l%")
 * - "rgb(...)" or "rgba(...)" -> converts to "h s% l%"
 * - Already components "h s% l%" or "h, s%, l%" -> returned as-is
 */
function toHslComponents(value: string): string {
  const v = (value || '').trim();
  if (!v) return '0 0% 0%';

  if (v.startsWith('#')) return hexToHSL(v);

  const rgb = parseRgbToHsl(v);
  if (rgb) return rgb;

  if (v.toLowerCase().startsWith('hsl(') && v.endsWith(')')) {
    return v.slice(4, -1).trim();
  }

  if (looksLikeHslComponents(v)) return v;

  return '0 0% 0%';
}

/**
 * Applies a preset by setting CSS variables to HSL components.
 * Also applies "dark" overrides if the document already has the "dark" class.
 */
function applyPresetTheme(presetName: string, persist: boolean): void {
  const presets = getAvailableThemePresets();
  const fallbackName = getDefaultPresetName(presets);
  const preset =
    presets.find((t) => t.name === presetName) ??
    presets.find((t) => t.name === fallbackName) ??
    presets[0];

  // Base variables
  Object.entries(preset.variables).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, toHslComponents(value));
  });

  // Dark overrides (only if in dark mode and preset provides overrides)
  if (document.documentElement.classList.contains('dark') && preset.dark) {
    Object.entries(preset.dark).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, toHslComponents(value));
    });
  }

  if (persist) {
    lsSet(THEME_KEY, presetName);
    lsRemove(DETECTION_KEY);
  }
}

/**
 * Applies custom theme map by setting CSS variables to HSL components.
 */
function applyCustomTheme(
  customColors: Record<string, string>,
  persist: boolean
): void {
  Object.entries(customColors).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, toHslComponents(value));
  });

  if (persist) {
    lsSet(THEME_KEY, 'Custom');
    lsSet(CUSTOM_KEY, JSON.stringify(customColors));
    lsRemove(DETECTION_KEY);
  }
}

/**
 * System detection mode: toggles dark/light class and chooses default presets.
 */
function handleDetectionMode(modeName: string, persist: boolean): void {
  if (persist) {
    lsSet(THEME_KEY, modeName);
    lsSet(DETECTION_KEY, 'system');
  }

  if (typeof window === 'undefined' || !window.matchMedia) return;

  const applyFromSystem = (): void => {
    const systemDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;
    if (systemDark) {
      document.documentElement.classList.add('dark');
      // Use a dark-friendly default preset
      applyPresetTheme('Default', false);
    } else {
      document.documentElement.classList.remove('dark');
      // Use a light-friendly default preset
      applyPresetTheme('Light', false);
    }
  };

  applyFromSystem();

  // React to future system changes if detection still enabled
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const listener = (e: MediaQueryListEvent): void => {
    if (lsGet(DETECTION_KEY) === 'system') {
      if (e.matches) {
        document.documentElement.classList.add('dark');
        applyPresetTheme('Default', false);
      } else {
        document.documentElement.classList.remove('dark');
        applyPresetTheme('Light', false);
      }
    }
  };
  media.addEventListener('change', listener);
}

/**
 * Public API
 */

export function setTheme(
  presetName: string,
  persist: boolean = false,
  customColors?: Record<string, string>
): void {
  if (typeof window === 'undefined') return; // SSR safeguard

  // Special detection modes (keep names compatible with existing UI)
  if (
    presetName === 'Detect: Light (System)' ||
    presetName === 'Detect: Default Dark (System)'
  ) {
    handleDetectionMode(presetName, persist);
    return;
  }

  if (presetName === 'Custom' && customColors) {
    applyCustomTheme(customColors, persist);
    return;
  }

  applyPresetTheme(presetName, persist);
}

export function getTheme(): string {
  if (typeof window === 'undefined') {
    const presets = getAvailableThemePresets();
    return getDefaultPresetName(presets);
  }

  const stored = lsGet(THEME_KEY);

  if (
    stored === 'Detect: Light (System)' ||
    stored === 'Detect: Default Dark (System)'
  ) {
    return stored;
  }

  if (stored === 'Custom') return 'Custom';

  const presets = getAvailableThemePresets();
  if (stored && presets.some((t) => t.name === stored)) {
    return stored;
  }

  return getDefaultPresetName(presets);
}

export function hasStoredThemePreference(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(lsGet(THEME_KEY));
}

export function applyStoredTheme(): void {
  if (typeof window === 'undefined') return;

  const theme = getTheme();

  if (theme === 'Custom') {
    const custom = lsGet(CUSTOM_KEY);
    if (custom) {
      try {
        const map = JSON.parse(custom) as Record<string, string>;
        applyCustomTheme(map, false);
        return;
      } catch {
        // If custom theme parsing fails, fall back to default
      }
    }
  }

  if (
    theme === 'Detect: Light (System)' ||
    theme === 'Detect: Default Dark (System)'
  ) {
    handleDetectionMode(theme, false);
    return;
  }

  applyPresetTheme(theme, false);
}
